import clsx from "clsx";
import { useInView } from "framer-motion";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Button } from "../../buttons/button";
import { Skeleton } from "../../skeleton";
import { EventCard } from "../event-card";
import { Event, EventsLabels } from "../types";

export interface EventsGridProps {
  events: Event[];
  labels: EventsLabels;
  loading?: boolean;
  eventDetailPagePath: string | null;
}

const SLICE_SIZE = 10;

export const EventsGrid: FC<EventsGridProps> = ({
  events,
  eventDetailPagePath,
  labels,
  loading,
}) => {
  const [visibleCount, setVisibleCount] = useState(SLICE_SIZE);

  useEffect(() => {
    setVisibleCount(SLICE_SIZE);
  }, [events]);

  const visibleEvents = useMemo(
    () => events.slice(0, Math.min(events.length, visibleCount)),
    [events, visibleCount]
  );

  const handleShowMore = () => {
    setVisibleCount((curr) => curr + SLICE_SIZE);
  };

  const getLinkToEventDetailPage = useCallback(
    (id: string | number) => `${eventDetailPagePath}?eventId=${id}`,
    [eventDetailPagePath]
  );

  // Send an event the first time the exhibition grid is viewed (and only first time)
  const gridRef = useRef<HTMLDivElement>(null);
  const isEventsGridVisible = useInView(gridRef);
  const hasSentViewEvent = useRef(false);
  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (isEventsGridVisible && !hasSentViewEvent.current) {
      pushEvent({
        event: GoogleAnalyticsEvent.ViewEventsList,
      });
      hasSentViewEvent.current = true;
    }
  }, [isEventsGridVisible, pushEvent]);

  return (
    <div>
      <div
        ref={gridRef}
        className={clsx(
          "grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3",
          "gap-x-12 gap-y-5 lg:gap-y-10 mt-8"
        )}
      >
        {loading
          ? new Array(6)
              .fill(null)
              .map((_, index) => <Skeleton key={index} height={300} width="100%" />)
          : visibleEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                labels={labels}
                pageLink={getLinkToEventDetailPage(event.id)}
              />
            ))}
      </div>
      {!loading && visibleEvents.length === 0 && (
        <div className="text-lg lg:text-2xl text-center w-full mt-8">
          {labels["events-no-results"]}
        </div>
      )}
      {visibleCount < events.length && (
        <div className="w-full flex justify-center mt-16">
          <Button variant="primary" onClick={handleShowMore}>
            {labels["events-load-more"]}
          </Button>
        </div>
      )}
    </div>
  );
};
